import { Injectable } from '@angular/core';
import { ajax, AjaxResponse } from 'rxjs/ajax';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
    CreateInvoiceRequestBody,
    InvoiceRequest,
    InvoiceRequestFieldIssue,
    ListInvoiceRequestsQuery,
    ListInvoiceRequestsResponse,
    ListStoresResponse,
    PatchInvoiceRequestBody,
    SelectSubmissionBody,
} from './interfaces/api';

// REST client for the invoice-request (tax-invoice/bill) API. Unlike the
// BaseRequester lambdas this API is a plain REST service authenticated with
// its own key (X-API-Key) and uses If-Match optimistic locking on writes.
@Injectable({
    providedIn: 'root',
})
export class InvoiceRequestService {
    // listAll follows nextCursor at 100 records/page up to this many pages.
    private static readonly maxListPages = 50;

    list(
        query: ListInvoiceRequestsQuery = {},
    ): Observable<ListInvoiceRequestsResponse> {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.set(key, String(value));
            }
        });
        const queryString = params.toString();
        return this.request<ListInvoiceRequestsResponse>(
            'GET',
            `/v1/invoice-requests${queryString ? `?${queryString}` : ''}`,
        );
    }

    // Follows cursor pagination until exhausted (capped at maxListPages).
    listAll(
        query: Omit<ListInvoiceRequestsQuery, 'limit' | 'cursor'> = {},
    ): Observable<InvoiceRequest[]> {
        return from(this.fetchAllPages(query));
    }

    create(body: CreateInvoiceRequestBody): Observable<InvoiceRequest> {
        return this.request<InvoiceRequest>('POST', '/v1/invoice-requests', {
            body,
        });
    }

    update(
        id: string,
        version: number,
        patch: PatchInvoiceRequestBody,
    ): Observable<InvoiceRequest> {
        return this.request<InvoiceRequest>(
            'PATCH',
            `/v1/invoice-requests/${encodeURIComponent(id)}`,
            { body: patch, ifMatch: version },
        );
    }

    // PDPA erasure — nulls all PII but keeps an anonymized stub. Idempotent.
    erase(id: string): Observable<void> {
        return this.request<void>(
            'DELETE',
            `/v1/invoice-requests/${encodeURIComponent(id)}`,
        );
    }

    // The authoritative storeId list. Every writer must name a store
    // identically or the unique (storeId, orderSn) index treats one order as
    // two, and two billing documents get issued. Worth caching for a few
    // minutes — it changes only when a store is added or retired.
    listStores(): Observable<ListStoresResponse> {
        return this.request<ListStoresResponse>('GET', '/v1/stores');
    }

    // Projects submissions[index] onto the record's invoice/requestType/
    // specialRequest, and clears needsReview whichever one is chosen —
    // including keeping the current one. Allowed from collecting, complete and
    // cancelled (which returns the record to collecting, the only way to put
    // its order number back to use), but never from issued.
    selectSubmission(
        id: string,
        version: number,
        index: number,
    ): Observable<InvoiceRequest> {
        const body: SelectSubmissionBody = { index };
        return this.request<InvoiceRequest>(
            'POST',
            `/v1/invoice-requests/${encodeURIComponent(id)}/select-submission`,
            { body, ifMatch: version },
        );
    }

    // collecting → complete
    confirm(id: string, version: number): Observable<InvoiceRequest> {
        return this.transition(id, version, 'confirm');
    }

    // complete → issued
    markIssued(id: string, version: number): Observable<InvoiceRequest> {
        return this.transition(id, version, 'mark-issued');
    }

    // collecting/complete → cancelled
    cancel(id: string, version: number): Observable<InvoiceRequest> {
        return this.transition(id, version, 'cancel');
    }

    private transition(
        id: string,
        version: number,
        action: 'confirm' | 'mark-issued' | 'cancel',
    ): Observable<InvoiceRequest> {
        return this.request<InvoiceRequest>(
            'POST',
            `/v1/invoice-requests/${encodeURIComponent(id)}/${action}`,
            { ifMatch: version },
        );
    }

    private async fetchAllPages(
        query: Omit<ListInvoiceRequestsQuery, 'limit' | 'cursor'>,
    ): Promise<InvoiceRequest[]> {
        const items: InvoiceRequest[] = [];
        let cursor: string | undefined;
        for (let page = 0; page < InvoiceRequestService.maxListPages; page++) {
            const response = await this.list({
                ...query,
                limit: 100,
                cursor,
            }).toPromise();
            if (!response) {
                break;
            }
            items.push(...response.items);
            if (!response.nextCursor) {
                return items;
            }
            cursor = response.nextCursor;
        }
        return items;
    }

    private request<T>(
        method: string,
        path: string,
        options: { body?: unknown; ifMatch?: number } = {},
    ): Observable<T> {
        const headers: Record<string, string> = {
            'X-API-Key': environment.invoiceRequestApiKey,
        };
        if (options.body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        if (options.ifMatch !== undefined) {
            headers['If-Match'] = `"${options.ifMatch}"`;
        }
        return ajax({
            url: `${environment.invoiceRequestApiEndpoint}${path}`,
            method,
            headers,
            body:
                options.body !== undefined
                    ? JSON.stringify(options.body)
                    : undefined,
        }).pipe(
            map((response: AjaxResponse) => response.response as T),
            catchError((error) =>
                throwError(InvoiceRequestService.toReadableError(error)),
            ),
        );
    }

    // Flattens the API's ErrorEnvelope into a message fit for grid banners
    // and toasts.
    private static toReadableError(error: any): Error {
        const envelope = error?.response?.error;
        if (!envelope?.code) {
            return error instanceof Error
                ? error
                : new Error(String(error?.message ?? error));
        }
        let message = `[${envelope.code}] ${envelope.message}`;
        if (
            envelope.code === 'VALIDATION_ERROR' &&
            Array.isArray(envelope.details)
        ) {
            const issues = envelope.details as InvoiceRequestFieldIssue[];
            message += ` — ${issues
                .map((issue) => `${issue.field}: ${issue.message}`)
                .join('; ')}`;
        } else if (envelope.code === 'VERSION_CONFLICT') {
            message += ' — the record changed on the server; reload and retry';
        } else if (
            envelope.code === 'CONFLICT' &&
            envelope.details?.existingId
        ) {
            message += ` — existing record: ${envelope.details.existingId}`;
        }
        return new Error(message);
    }
}
